"use client";

import { useCallback, useEffect, useState } from "react";

export type StorageEstimate = {
  usageBytes: number | null;
  quotaBytes: number | null;
  loading: boolean;
  clearing: boolean;
  refresh: () => Promise<void>;
  clearCache: () => Promise<void>;
};

function storageEstimateSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.storage?.estimate;
}

export function useStorageEstimate(): StorageEstimate {
  const [usageBytes, setUsageBytes] = useState<number | null>(null);
  const [quotaBytes, setQuotaBytes] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const runEstimate = useCallback(async () => {
    if (!storageEstimateSupported()) {
      setLoading(false);
      return;
    }
    try {
      const estimate = await navigator.storage.estimate();
      setUsageBytes(estimate.usage ?? null);
      setQuotaBytes(estimate.quota ?? null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!storageEstimateSupported()) return;
    setLoading(true);
    await runEstimate();
  }, [runEstimate]);

  useEffect(() => {
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (!cancelled) void runEstimate();
    });
    return () => {
      cancelled = true;
    };
  }, [runEstimate]);

  const clearCache = useCallback(async () => {
    if (typeof caches === "undefined") return;
    setClearing(true);
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
      await refresh();
    } finally {
      setClearing(false);
    }
  }, [refresh]);

  return { usageBytes, quotaBytes, loading, clearing, refresh, clearCache };
}
